// The editor of additional-field DEFINITIONS (task F, 2026-10-20), shared by two screens:
//   * Management -> Field templates (pages/admin/FieldsCard.jsx): `base` "/admin/fields",
//     `kind` "template" — an admin's templates, copied into projects, never linked;
//   * a project's Field settings (pages/ProjectFieldSettings.jsx): `base`
//     "/projects/{id}/fields", `kind` "project" — the project's own fields, edited by its
//     curators and admins (`canEdit`); members read them.
//
//   GET    {base}            [{id, target, key, label, help_text, field_type, options, required,
//                              sort_order, is_active, template_id, ...}] (a template adds
//                              default_for_new_projects and project_ids, a project's field
//                              value_count)
//   POST   {base}            {target, label, field_type, help_text, options, required, sort_order
//                              [, default_for_new_projects]}; the key is made from the label
//   PATCH  {base}/{id}       the changed attributes (never target, key or type); {is_active: true}
//                              activates a deactivated one again
//   DELETE {base}/{id}       {deleted, deactivated}: one with values (a template: with copies) is
//                              only deactivated
import { useState } from "react";
import { api } from "../api";
import { ErrorNote, Loading } from "../components/Feedback";
import { Badge, Btn, Card, Field, Input, Select, Textarea } from "../components/Shared";
import { useApi } from "../useApi";
import { CHOICE_TYPES, FIELD_TARGETS, FIELD_TYPES, targetLabel, typeLabel } from "./fieldTypes";
import { AskDialog, LEGEND, SR_ONLY, SuccessNote } from "../pages/admin/parts";

const TH = { padding: "6px 8px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "#374151" };
const TD = { padding: "8px", verticalAlign: "top" };
const CHECK_ROW = { display: "inline-flex", gap: 8, alignItems: "center", fontSize: 14, color: "#1a1a1a" };

const EMPTY_FORM = { target: "project", label: "", field_type: "short_text", help_text: "", options: [],
                     required: false, sort_order: "0", default_for_new_projects: false };

function formOf(d) {
  return { target: d.target, label: d.label, field_type: d.field_type, help_text: d.help_text || "",
           options: [...(d.options || [])], required: !!d.required, sort_order: String(d.sort_order ?? 0),
           default_for_new_projects: !!d.default_for_new_projects };
}

/** The body of a POST, or of a PATCH with only what changed against `stored`. A project's field
 *  (`template` false) has no default_for_new_projects. */
export function fieldBody(form, stored = null, template = true) {
  const body = {
    label: form.label.trim(),
    help_text: form.help_text.trim() || null,
    options: CHOICE_TYPES.includes(form.field_type) ? form.options.map(o => o.trim()).filter(Boolean) : [],
    required: !!form.required,
    sort_order: Number.parseInt(form.sort_order, 10) || 0,
  };
  if (template) body.default_for_new_projects = !!form.default_for_new_projects;
  if (!stored) return { target: form.target, field_type: form.field_type, ...body };
  const before = { label: stored.label, help_text: stored.help_text || null, options: stored.options || [],
                   required: !!stored.required, sort_order: stored.sort_order ?? 0,
                   default_for_new_projects: !!stored.default_for_new_projects };
  return Object.fromEntries(Object.entries(body).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(before[k])));
}

// The options of a choice field: one line each, added, removed and moved up.
function OptionsEditor({ options, onChange }) {
  const [next, setNext] = useState("");
  const add = () => {
    const o = next.trim();
    if (o && !options.includes(o)) onChange([...options, o]);
    setNext("");
  };
  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
      <legend style={LEGEND}>Options<span aria-hidden="true" style={{ color: "#b91c1c", marginLeft: 4 }}>*</span></legend>
      {options.length === 0 && <p style={{ margin: "0 0 8px", fontSize: 13, color: "#6b7280" }}>No options yet; a choice field needs at least one.</p>}
      <ol style={{ margin: "0 0 8px", paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 }}>
        {options.map((o, i) => (
          <li key={`${o}-${i}`}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input aria-label={`Option ${i + 1}`} value={o}
                     onChange={e => onChange(options.map((x, j) => (j === i ? e.target.value : x)))} />
              <button type="button" aria-label={`Move ${o || `option ${i + 1}`} up`} disabled={i === 0}
                      onClick={() => onChange(options.map((x, j) => (j === i - 1 ? options[i] : j === i ? options[i - 1] : x)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", padding: "6px 9px", cursor: i === 0 ? "default" : "pointer" }}>↑</button>
              <button type="button" aria-label={`Remove ${o || `option ${i + 1}`}`}
                      onClick={() => onChange(options.filter((_, j) => j !== i))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", padding: "6px 9px", cursor: "pointer" }}>×</button>
            </div>
          </li>
        ))}
      </ol>
      <div style={{ display: "flex", gap: 8 }}
           onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}>
        <Input aria-label="New option" value={next} onChange={e => setNext(e.target.value)} />
        <Btn variant="secondary" onClick={add} disabled={!next.trim()}>Add option</Btn>
      </div>
    </fieldset>
  );
}

// Create (no `stored`) or edit (`stored`: target, key and type are shown, not editable).
function DefinitionForm({ base, template, noun, stored, onSaved, onCancel }) {
  const [form, setForm] = useState(() => (stored ? formOf(stored) : { ...EMPTY_FORM }));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const flag = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));
  const choice = CHOICE_TYPES.includes(form.field_type);

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let saved;
      if (stored) {
        const patch = fieldBody(form, stored, template);
        saved = Object.keys(patch).length ? await api.patch(`${base}/${stored.id}`, patch) : stored;
      } else {
        saved = await api.post(base, fieldBody(form, null, template));
      }
      onSaved(saved, !stored);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const heading = stored ? `Edit ${stored.label}` : `New ${noun}`;
  return (
    <Card style={{ marginBottom: 20, padding: "20px 24px" }}>
      <form onSubmit={submit} aria-label={heading} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{heading}</h3>
        <ErrorNote error={error} />
        <Field label="Label" required><Input value={form.label} onChange={set("label")} maxLength={128} /></Field>
        {stored ? (
          <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
            For {targetLabel(stored.target).toLowerCase()}s · {typeLabel(stored.field_type)} · key{" "}
            <code>{stored.key}</code>. The target, key and type never change; define a new field instead.
          </p>
        ) : (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Field label="Applies to" required style={{ flex: "1 1 200px" }}>
              <Select options={FIELD_TARGETS.map(t => ({ value: t.value, label: t.label }))} value={form.target}
                      onChange={e => setForm(f => ({ ...f, target: e.target.value || "project" }))} />
            </Field>
            <Field label="Type" required style={{ flex: "1 1 200px" }}>
              <Select options={FIELD_TYPES} value={form.field_type}
                      onChange={e => setForm(f => ({ ...f, field_type: e.target.value || "short_text" }))} />
            </Field>
          </div>
        )}
        {choice && <OptionsEditor options={form.options} onChange={options => setForm(f => ({ ...f, options }))} />}
        <Field label="Help text" hint="Shown under the field where its value is filled in.">
          <Textarea value={form.help_text} onChange={set("help_text")} maxLength={500} />
        </Field>
        <Field label="Sort order" hint="Lower numbers come first.">
          <Input type="number" value={form.sort_order} onChange={set("sort_order")} style={{ maxWidth: 140 }} />
        </Field>
        <label style={CHECK_ROW}>
          <input type="checkbox" checked={form.required} onChange={flag("required")} />
          {template ? "Required (in the copies made from now on)" : "Required in this project"}
        </label>
        {template && (
          <label style={CHECK_ROW}>
            <input type="checkbox" checked={form.default_for_new_projects} onChange={flag("default_for_new_projects")} />
            Default for new projects (copied into every project created from now on)
          </label>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <Btn type="submit" disabled={busy || !form.label.trim() || (choice && !form.options.some(o => o.trim()))}>
            {stored ? `Save ${noun}` : `Create ${noun}`}
          </Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}

function SrName({ name }) {
  return <span style={SR_ONLY}> {name}</span>;
}

function DefinitionTable({ caption, target, fields, template, canEdit, onEdit, onDelete, onActivate, busy }) {
  const t = FIELD_TARGETS.find(x => x.value === target);
  const cols = template
    ? ["Label", "Type", "Options", "Required", "Default for new projects", "Status", "Projects"]
    : ["Label", "Type", "Options", "Required", "From template", "Status", "Values"];
  return (
    <div style={{ overflowX: "auto", marginBottom: 24 }}>
      <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <caption style={{ textAlign: "left", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{caption}</caption>
        <thead>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            {cols.map(c => <th key={c} scope="col" style={TH}>{c}</th>)}
            {canEdit && <th scope="col" style={TH}><span style={SR_ONLY}>Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {fields.length === 0 && (
            <tr><td colSpan={cols.length + (canEdit ? 1 : 0)} style={{ ...TD, color: "#4b5563" }}>
              No {t.label.toLowerCase()} {template ? "templates" : "fields"}.
            </td></tr>
          )}
          {fields.map(f => (
            <tr key={f.id} style={{ borderBottom: "1px solid #f3f4f6", opacity: f.is_active ? 1 : 0.75 }}>
              <th scope="row" style={{ ...TD, textAlign: "left", fontWeight: 600 }}>
                {f.label}
                <div style={{ fontSize: 12, fontWeight: 400, color: "#4b5563", fontFamily: "'JetBrains Mono', monospace" }}>{f.key}</div>
                {f.help_text && <div style={{ fontSize: 12, fontWeight: 400, color: "#4b5563" }}>{f.help_text}</div>}
              </th>
              <td data-label="Type" style={TD}>{typeLabel(f.field_type)}</td>
              <td data-label="Options" style={TD}>{(f.options || []).length ? f.options.join(", ") : "—"}</td>
              <td data-label="Required" style={TD}>{f.required ? "yes" : "no"}</td>
              {template
                ? <td data-label={cols[4]} style={TD}>{f.default_for_new_projects ? "yes" : "no"}</td>
                : <td data-label={cols[4]} style={TD}>{f.template_id ? "yes" : "no"}</td>}
              <td data-label="Status" style={TD}>{f.is_active ? <Badge color="green">active</Badge> : <Badge color="gray">deactivated</Badge>}</td>
              <td data-label={cols[6]} style={TD}>{template ? (f.project_ids || []).length : (f.value_count ?? 0)}</td>
              {canEdit && (
                <td style={{ ...TD, whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Btn variant="secondary" onClick={() => onEdit(f)}>Edit<SrName name={f.label} /></Btn>
                    {f.is_active
                      ? <Btn variant="danger" onClick={() => onDelete(f)}>Delete<SrName name={f.label} /></Btn>
                      : <Btn variant="secondary" disabled={busy} onClick={() => onActivate(f)}>Activate<SrName name={f.label} /></Btn>}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function deleteText(f, template) {
  if (template) {
    const n = (f.project_ids || []).length;
    return n
      ? `${f.label} was copied into ${n} project${n === 1 ? "" : "s"}, so it is deactivated instead: it is no longer offered or copied, the copies stay as they are, and it can be activated again.`
      : `${f.label} is not copied into any project; it is deleted.`;
  }
  return f.value_count
    ? `${f.label} has ${f.value_count} value${f.value_count === 1 ? "" : "s"}, so it is deactivated instead: the values stay readable, and it can be activated again.`
    : `${f.label} has no values; it is deleted.`;
}

/** The list of definitions by target, with New / Edit / Delete / Activate when `canEdit`.
 *  `kind` "template" | "project"; `onChanged` after every change (for a parent that shows
 *  values). */
export default function FieldDefinitionEditor({ base, kind, canEdit = true, onChanged }) {
  const template = kind === "template";
  const noun = template ? "template" : "field";
  const list = useApi(base);
  const [mode, setMode] = useState(null);       // null | {kind: "new"} | {kind: "edit", field}
  const [deleting, setDeleting] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fields = Array.isArray(list.data) ? list.data : [];

  const done = text => { setMode(null); setNotice(text); setError(null); list.reload(); if (onChanged) onChanged(); };

  const remove = async () => {
    const f = deleting;
    setBusy(true);
    setError(null);
    try {
      const r = await api.del(`${base}/${f.id}`);
      setDeleting(null);
      done(r && r.deactivated
        ? (template
          ? `${f.label} is copied into projects, so it was deactivated, not deleted: it is no longer offered or copied, and the copies stay as they are.`
          : `${f.label} has values, so it was deactivated, not deleted: its values stay readable, and nobody fills it in any more.`)
        : `${f.label} was deleted.`);
    } catch (err) {
      setError(err);
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };
  const activate = async f => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`${base}/${f.id}`, { is_active: true });
      done(`${f.label} is active again.`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <ErrorNote error={error || list.error} />
      <SuccessNote>{notice}</SuccessNote>
      {canEdit && !mode && (
        <div style={{ marginBottom: 18 }}>
          <Btn onClick={() => { setNotice(null); setMode({ kind: "new" }); }}>New {noun}</Btn>
        </div>
      )}
      {mode?.kind === "new" && (
        <DefinitionForm base={base} template={template} noun={noun} onCancel={() => setMode(null)}
                        onSaved={saved => done(`${saved.label} was created (key ${saved.key}).`)} />
      )}
      {mode?.kind === "edit" && (
        <DefinitionForm key={mode.field.id} base={base} template={template} noun={noun} stored={mode.field}
                        onCancel={() => setMode(null)} onSaved={saved => done(`${saved.label} was saved.`)} />
      )}
      {list.loading && !list.data ? <Loading /> : FIELD_TARGETS.map(t => (
        <DefinitionTable key={t.value} target={t.value} template={template} canEdit={canEdit} busy={busy}
                         caption={template ? `${t.label} field templates` : t.plural}
                         fields={fields.filter(f => f.target === t.value)}
                         onEdit={f => { setNotice(null); setMode({ kind: "edit", field: f }); }}
                         onDelete={f => setDeleting(f)} onActivate={activate} />
      ))}
      {deleting && (
        <AskDialog title={`Delete ${deleting.label}`} confirmLabel={`Delete ${noun}`} busy={busy}
                   onConfirm={remove} onClose={() => setDeleting(null)}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>{deleteText(deleting, template)}</p>
        </AskDialog>
      )}
    </div>
  );
}
